import {
  Box,
  Divider,
  Grid2,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import countries from 'i18n-iso-countries'
import i18n from 'i18next'
import type { JSX } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { getDeviceDetails } from '@/api/Device'
import Card from '@/components/Card/Card'
import CardBody from '@/components/Card/CardBody'
import CardHeader from '@/components/Card/CardHeader'
import Indicators from '@/pages/Device/Components/Indicators'
import type { Device } from '@/pages/Device/Device'
import deviceType from '@/pages/Device/DeviceType'
import UpdateForm from '@/pages/Device/UpdateForm'
import { labelSx } from '@/styles/MuiComponentStyles'

export default function DeviceInfoDetails(): JSX.Element {
  const { uid } = useParams<{ uid: string }>()
  const [t] = useTranslation('app')
  const [device, setDevice] = useState<Device>()

  const fetchData = useCallback(async () => {
    if (!uid) {
      return <></>
    }
    try {
      const data = await getDeviceDetails(uid)
      setDevice(data)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }, [uid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  if (!device) {
    return <></>
  }

  return (
    <Box data-testid="device-info-root" sx={{ padding: '24px' }}>
      <Card>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <CardHeader>
            <Typography variant="h4">{t('common:general.info')}</Typography>
          </CardHeader>
          <Indicators device={device} />
        </Stack>
        <CardBody>
          <Grid2 container spacing={2} sx={{ padding: 3 }}>
            <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                data-testid="field-device-type"
                variant="standard"
                label={t('di:device.type')}
                slotProps={{
                  inputLabel: { sx: labelSx, shrink: true },
                  input: { disableUnderline: true },
                }}
                fullWidth
                value={(device?.type && deviceType[device.type]) ?? ''}
                disabled={true}
              />
            </Grid2>

            {device.type.startsWith('zkl-3000') && (
              <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  variant="standard"
                  label={t('di:device.version')}
                  slotProps={{
                    inputLabel: { sx: labelSx, shrink: true },
                    input: { disableUnderline: true },
                  }}
                  fullWidth
                  value={device?.version || '-'}
                  disabled={true}
                />
              </Grid2>
            )}

            <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                variant="standard"
                label={t('di:device.country')}
                slotProps={{
                  inputLabel: { sx: labelSx, shrink: true },
                  input: { disableUnderline: true },
                }}
                fullWidth
                value={
                  device?.country
                    ? countries.getName(device.country, i18n.language, {
                        select: 'official',
                      })
                    : '-'
                }
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                variant="standard"
                label={t('di:device.ownerLabel')}
                slotProps={{
                  inputLabel: { sx: labelSx, shrink: true },
                  input: { disableUnderline: true },
                }}
                fullWidth
                value={device?.label ?? '-'}
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                variant="standard"
                label={t('di:device.ncmType')}
                slotProps={{
                  inputLabel: { sx: labelSx, shrink: true },
                  input: { disableUnderline: true },
                }}
                fullWidth
                value={device?.ncmType?.toUpperCase() ?? '-'}
                disabled={true}
              />
            </Grid2>
          </Grid2>
          <Divider />
          <Box sx={{ padding: '24px' }}>
            <UpdateForm device={device} onUpdated={() => fetchData()} />
          </Box>
        </CardBody>
      </Card>
    </Box>
  )
}
